import { UseDisclosureReturn } from "@chakra-ui/react"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import { stripTrailingSlashes } from "@pathwright/utils/src/url/path"
import { useCallback } from "react"
import { AugmentationToolbarHookReturn } from "./AugmentationToolbar"
import { useQueryParamState } from "./useQueryParamHistory"

function splitPathLocation(path: string) {
  const hashIndex = path.indexOf("#")
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ""
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const searchIndex = withoutHash.indexOf("?")
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : ""
  const pathname =
    searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash

  return { pathname, search, hash }
}

/** Remove trailing path segments for a nested panel route key (e.g. `access`, `contributors/new`). */
export function getParentPanelPathForRouteKey(
  path: string,
  routeKey: string
): string | null {
  const segmentsToRemove = routeKey.split("/").filter(Boolean).length
  if (!segmentsToRemove) return null

  const { pathname, search, hash } = splitPathLocation(path)
  const segments = stripTrailingSlashes(pathname).split("/").filter(Boolean)

  if (segments.length <= segmentsToRemove) return null

  return `/${segments.slice(0, -segmentsToRemove).join("/")}${search}${hash}`
}

type UseAugmentationPanelEscapeProps = {
  toolbar: AugmentationToolbarHookReturn
  disclosure?: UseDisclosureReturn
  queryParamKey: string
  hasOpenDialog?: boolean
}

function claimEscapeEvent(e: KeyboardEvent) {
  e.preventDefault()
  e.stopPropagation()
  e.stopImmediatePropagation()
}

function useAugmentationPanelEscape({
  toolbar,
  disclosure,
  queryParamKey,
  hasOpenDialog
}: UseAugmentationPanelEscapeProps) {
  const { queryParamPath, setQueryParamPath } =
    useQueryParamState(queryParamKey)

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (!disclosure?.open) return false
      if (hasOpenDialog) return false

      const currentRouteKey = toolbar.stack[toolbar.stack.length - 1]?.key

      if (toolbar.stack.length > 1 && queryParamPath && currentRouteKey) {
        const parentPath = getParentPanelPathForRouteKey(
          queryParamPath,
          currentRouteKey
        )

        if (parentPath) {
          claimEscapeEvent(e)
          setQueryParamPath(parentPath)
          return
        }
      }

      claimEscapeEvent(e)
      disclosure.onClose()
    },
    [
      disclosure,
      hasOpenDialog,
      queryParamPath,
      setQueryParamPath,
      toolbar.stack
    ]
  )

  useKeyControl("Escape", handleEscape)

  return handleEscape
}

export default useAugmentationPanelEscape
